import { useState } from 'react';
import { useAtom } from 'jotai';
import { X, Github } from 'lucide-react';
import { currentProjectAtom } from '@/stores/editorStore';
import { apiRequest } from '@/lib/api';
import type { Project } from '@shared/types';

interface GitHubImportModalProps {
  isOpen: boolean;
  onClose: () => void;
  onProjectCreated?: () => void;
}

export default function GitHubImportModal({ isOpen, onClose, onProjectCreated }: GitHubImportModalProps) {
  const [, setCurrentProject] = useAtom(currentProjectAtom);
  const [githubUrl, setGithubUrl] = useState('');
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [isImporting, setIsImporting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isOpen) return null;

  // Auto-extract repo name from GitHub URL
  const handleUrlChange = (url: string) => {
    setGithubUrl(url);
    setError(null);

    // Extract repo name from URL
    const match = url.match(/github\.com\/([^\/]+)\/([^\/\.]+)/);
    if (match) {
      const repoName = match[2];
      setName(repoName);
    }
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);

    if (!githubUrl.trim()) {
      setError('GitHub repository URL is required');
      return;
    }

    if (!githubUrl.includes('github.com')) {
      setError('Please enter a valid GitHub URL');
      return;
    }

    if (!name.trim()) {
      setError('Project name is required');
      return;
    }

    setIsImporting(true);

    try {
      const response = await apiRequest<{ project: Project }>('post', '/projects', {
        name: name.trim(),
        description: description.trim() || undefined,
        template: 'github',
        github_url: githubUrl.trim(),
      });

      setCurrentProject(response.project);
      setGithubUrl('');
      setName('');
      setDescription('');
      onClose();
      onProjectCreated?.();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to import from GitHub');
    } finally {
      setIsImporting(false);
    }
  };

  const handleClose = () => {
    if (!isImporting) {
      setGithubUrl('');
      setName('');
      setDescription('');
      setError(null);
      onClose();
    }
  };

  return (
    <div className="fixed inset-0 bg-background/80 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-card border rounded-lg shadow-lg max-w-2xl w-full max-h-[90vh] overflow-auto">
        {/* Header with GitHub Branding */}
        <div className="flex items-center justify-between p-6 border-b bg-gradient-to-r from-gray-900 to-gray-800">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 bg-white rounded-full flex items-center justify-center">
              <Github className="w-6 h-6 text-gray-900" />
            </div>
            <div>
              <h2 className="text-xl font-semibold text-white">Import from GitHub</h2>
              <p className="text-xs text-gray-300">Clone any public or private repository</p>
            </div>
          </div>
          <button
            onClick={handleClose}
            disabled={isImporting}
            className="p-1 hover:bg-gray-700 rounded-md transition-colors disabled:opacity-50 text-white"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Form */}
        <form onSubmit={handleSubmit} className="p-6 space-y-6">
          {/* GitHub URL */}
          <div>
            <label htmlFor="github_url" className="block text-sm font-medium mb-2">
              GitHub Repository URL *
            </label>
            <div className="relative">
              <Github className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-muted-foreground" />
              <input
                id="github_url"
                type="text"
                value={githubUrl}
                onChange={(e) => handleUrlChange(e.target.value)}
                placeholder="https://github.com/username/repository"
                disabled={isImporting}
                className="w-full pl-10 pr-3 py-2 bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 font-mono text-sm"
                autoFocus
              />
            </div>
            <p className="text-xs text-muted-foreground mt-1">
              For private repos, use: https://YOUR_TOKEN@github.com/username/repository
            </p>
          </div>

          {/* Project Name (Auto-filled) */}
          <div>
            <label htmlFor="name" className="block text-sm font-medium mb-2">
              Project Name *
            </label>
            <input
              id="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="my-awesome-project"
              disabled={isImporting}
              className="w-full px-3 py-2 bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50"
            />
            <p className="text-xs text-muted-foreground mt-1">
              Auto-filled from repository name (you can change it)
            </p>
          </div>

          {/* Description */}
          <div>
            <label htmlFor="description" className="block text-sm font-medium mb-2">
              Description (optional)
            </label>
            <textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="What is this project about?"
              disabled={isImporting}
              rows={3}
              className="w-full px-3 py-2 bg-background border rounded-md focus:outline-none focus:ring-2 focus:ring-primary disabled:opacity-50 resize-none"
            />
          </div>

          {/* Info Box */}
          <div className="p-3 bg-blue-500/10 border border-blue-500/20 rounded-md">
            <p className="text-sm text-blue-400 font-medium mb-1">What happens next?</p>
            <ul className="text-xs text-muted-foreground space-y-1">
              <li>• Repository will be cloned to your isolated workspace</li>
              <li>• Dependencies will be automatically installed (if package.json exists)</li>
              <li>• You can start coding immediately in your IDE</li>
            </ul>
          </div>

          {/* Error */}
          {error && (
            <div className="p-3 bg-destructive/10 border border-destructive/20 rounded-md">
              <p className="text-sm text-destructive">{error}</p>
            </div>
          )}

          {/* Actions */}
          <div className="flex gap-3 justify-end pt-4 border-t">
            <button
              type="button"
              onClick={handleClose}
              disabled={isImporting}
              className="px-4 py-2 text-sm border rounded-md hover:bg-muted transition-colors disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isImporting}
              className="px-4 py-2 text-sm bg-gray-900 text-white rounded-md hover:bg-gray-800 transition-colors disabled:opacity-50 flex items-center gap-2"
            >
              {isImporting ? (
                <>
                  <span className="inline-block w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  Cloning Repository...
                </>
              ) : (
                <>
                  <Github className="w-4 h-4" />
                  Import from GitHub
                </>
              )}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
